import { normalizeSpellReplacements } from "@/lib/spellcheckCaret";

/**
 * Barra de chips con sugerencias ortográficas (estilo mensajería).
 * No corrige sola: el usuario elige la palabra (o la ignora para no volver a verla).
 * Las correcciones aceptadas se recuerdan para mensajes repetitivos.
 */
export default function SpellcheckSuggestionBar({
  value = "",
  matches = [],
  onApply,
  onIgnore,
  maxItems = 5,
  testId = "spellcheck-suggestion-bar",
}) {
  const text = String(value || "");
  const items = [];
  for (const match of matches || []) {
    if (items.length >= maxItems) break;
    const suggestions = normalizeSpellReplacements(match);
    const suggestion = suggestions[0];
    if (!suggestion) continue;
    const offset = Number(match?.offset) || 0;
    const length = Number(match?.length) || 0;
    const word = match?.word || (length > 0 ? text.slice(offset, offset + length) : "");
    if (!word || word.toLowerCase() === String(suggestion).toLowerCase()) continue;
    items.push({ match, word, suggestion, key: `${offset}:${length}:${suggestion}` });
  }

  if (!items.length) return null;

  return (
    <div
      className="flex flex-wrap items-center gap-1.5 rounded-md border border-amber-200/80 bg-amber-50/70 px-2 py-1.5"
      data-testid={testId}
      role="group"
      aria-label="Sugerencias ortográficas"
    >
      <span className="text-[10px] font-medium text-amber-900/80 shrink-0">¿Quiso decir?</span>
      {items.map((item) => (
        <span
          key={item.key}
          className="inline-flex items-center gap-0.5 rounded-full border border-amber-300/90 bg-white pl-2 pr-0.5 py-0.5 text-[11px] text-[#0F2D5C]"
        >
          <button
            type="button"
            className="inline-flex items-center gap-1 hover:bg-amber-100 rounded-full px-1 py-0.5 focus:outline-none focus:ring-2 focus:ring-[#0F2D5C]/25"
            onClick={() => onApply?.(item.match, item.suggestion)}
            data-testid={`${testId}-chip`}
            title={`Reemplazar «${item.word}» por «${item.suggestion}» (se recordará)`}
          >
            <span className="line-through text-slate-400 decoration-rose-400">{item.word}</span>
            <span aria-hidden>→</span>
            <span className="font-semibold">{item.suggestion}</span>
          </button>
          {onIgnore ? (
            <button
              type="button"
              className="rounded-full px-1.5 py-0.5 text-[10px] text-slate-500 hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0F2D5C]/25"
              onClick={() => onIgnore?.(item.match)}
              data-testid={`${testId}-ignore`}
              title={`Mantener «${item.word}» y no sugerir de nuevo`}
              aria-label={`Ignorar sugerencia para ${item.word}`}
            >
              ×
            </button>
          ) : null}
        </span>
      ))}
    </div>
  );
}
